import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Spinner, Button, Input, InputGroup, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const ORANGE_COLORS = {
  primary: '#ff6b35',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const pageHeadingStyle = {
  color: "var(--vz-body-color)",
};

const darkIconChip = {
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "1px solid rgba(255, 91, 0, 0.35)",
  background: "rgba(255, 91, 0, 0.12)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const darkIconGlyph = {
  color: "#FF5B00",
  fontSize: 18,
};

const chipRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const iconChip = (color = ORANGE_COLORS.primary) => ({
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: `0.5px solid ${chipRgba(color, 0.24)}`,
  background: chipRgba(color, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const iconGlyph = (color = ORANGE_COLORS.primary, size = 16) => ({
  color,
  fontSize: size,
  lineHeight: 1,
});

// Mock data for demo
const MOCK_CONNECTORS = [
  {
    id: 1,
    name: "Office 365",
    type: "Cloud Service",
    category: "Productivity",
    status: "Active",
    description: "Microsoft 365 Email, SharePoint, Teams logs",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 15),
    dataCollected: 15420,
    icon: "briefcase",
    color: ORANGE_COLORS.primary
  },
  {
    id: 2,
    name: "SAP",
    type: "ERP System",
    category: "Enterprise",
    status: "Active",
    description: "SAP ERP application logs and audit trails",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 30),
    dataCollected: 8930,
    icon: "database",
    color: ORANGE_COLORS.primary
  },
  {
    id: 3,
    name: "AWS CloudTrail",
    type: "Cloud Service",
    category: "Infrastructure",
    status: "Active",
    description: "AWS infrastructure and service logs",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 5),
    dataCollected: 23150,
    icon: "cloud",
    color: ORANGE_COLORS.primary
  },
  {
    id: 4,
    name: "Azure AD",
    type: "Identity Provider",
    category: "Identity",
    status: "Inactive",
    description: "Azure Active Directory authentication logs",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    dataCollected: 0,
    icon: "shield",
    color: ORANGE_COLORS.primary
  },
  {
    id: 5,
    name: "Google Workspace",
    type: "Cloud Service",
    category: "Productivity",
    status: "Active",
    description: "Gmail, Drive, Admin activity logs",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 20),
    dataCollected: 12340,
    icon: "mail",
    color: ORANGE_COLORS.primary
  },
  {
    id: 6,
    name: "Salesforce",
    type: "SaaS Application",
    category: "CRM",
    status: "Not Configured",
    description: "CRM activity and security logs",
    connectionType: "API",
    lastSync: null,
    dataCollected: 0,
    icon: "trending-up",
    color: ORANGE_COLORS.primary
  },
  {
    id: 7,
    name: "Okta",
    type: "Identity Provider",
    category: "Identity",
    status: "Active",
    description: "Authentication and access management logs",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 10),
    dataCollected: 5670,
    icon: "user-check",
    color: ORANGE_COLORS.primary
  },
  {
    id: 8,
    name: "Palo Alto Firewall",
    type: "Network Device",
    category: "Security",
    status: "Not Configured",
    description: "Network traffic and security logs",
    connectionType: "Syslog",
    lastSync: null,
    dataCollected: 0,
    icon: "shield",
    color: ORANGE_COLORS.primary
  },
  {
    id: 9,
    name: "Slack",
    type: "Communication",
    category: "Productivity",
    status: "Active",
    description: "Workspace activity and security events",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 8),
    dataCollected: 3240,
    icon: "message-circle",
    color: ORANGE_COLORS.primary
  },
  {
    id: 10,
    name: "GitHub",
    type: "DevOps",
    category: "Development",
    status: "Not Configured",
    description: "Repository activity and security alerts",
    connectionType: "Webhook",
    lastSync: null,
    dataCollected: 0,
    icon: "git-branch",
    color: ORANGE_COLORS.primary
  },
  {
    id: 11,
    name: "Cisco Meraki",
    type: "Network Device",
    category: "Security",
    status: "Inactive",
    description: "Network security and traffic logs",
    connectionType: "API",
    lastSync: new Date(Date.now() - 1000 * 60 * 60 * 48),
    dataCollected: 0,
    icon: "wifi",
    color: ORANGE_COLORS.primary
  },
  {
    id: 12,
    name: "Jira",
    type: "Project Management",
    category: "Productivity",
    status: "Not Configured",
    description: "Issue tracking and project activity",
    connectionType: "API",
    lastSync: null,
    dataCollected: 0,
    icon: "trello",
    color: ORANGE_COLORS.primary
  }
];

const getStatusConfig = (status) => {
  switch (status) {
    case "Active":
      return { color: '#198754', icon: 'check-circle' };
    case "Inactive":
      return { color: '#ffc107', icon: 'alert-circle' };
    case "Not Configured":
      return { color: '#6c757d', icon: 'settings' };
    default:
      return { color: '#6c757d', icon: 'help-circle' };
  }
};

const formatLastSync = (date) => {
  if (!date) return "Never synced";
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

// Stats Tile Component
const StatTile = ({ label, value, icon, color, borderColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card 
      className="card-animate" 
      style={{ 
        ...cardShell,
        border: "1px solid var(--vz-border-color)",
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 16px rgba(0,0,0,0.08)' : cardShell.boxShadow,
        cursor: 'pointer',
        minHeight: '140px',
        height: '100%'
      }}
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardBody style={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%'
      }}>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="text-uppercase fw-medium text-muted mb-0" style={{ 
              fontSize: '11px',
              letterSpacing: '0.5px'
            }}>
              {label}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div style={iconChip(color)}>
              <FeatherIcon icon={icon} style={iconGlyph(color, 14)} />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between mt-auto pt-3">
          <div>
            <h4 
              className="fs-22 fw-semibold ff-secondary mb-0" 
              style={{ color: color }}
            >
              {value !== null && value !== undefined ? value.toLocaleString() : '0'}
            </h4>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const ConnectorCard = ({ connector, onConfigure, onTest, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);
  const statusConfig = getStatusConfig(connector.status);
  
  return (
    <Card 
      className="mb-3 h-100"
      style={{ 
        ...cardShell,
        border: "1px solid var(--vz-border-color)",
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 16px rgba(0,0,0,0.08)' : cardShell.boxShadow
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center justify-content-center me-3" style={iconChip(ORANGE_COLORS.primary)}>
              <FeatherIcon icon={connector.icon} style={iconGlyph(ORANGE_COLORS.primary, 14)} />
            </div>
            <div>
              <h5 className="mb-1" style={{ fontSize: '16px' }}>{connector.name}</h5>
              <p className="text-muted small mb-0">{connector.type}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FeatherIcon icon={statusConfig.icon} size={14} style={{ color: statusConfig.color }} />
            <span className="small fw-semibold" style={{ color: statusConfig.color }}>
              {connector.status}
            </span>
          </div>
        </div>
        
        <p className="text-muted small mb-3" style={{ minHeight: '40px' }}>
          {connector.description}
        </p>
        
        <div className="mb-2">
          <span 
            className="badge me-2" 
            style={{ 
              backgroundColor: "rgba(255,255,255,0.04)",
              color: ORANGE_COLORS.primary,
              border: "1px solid var(--vz-border-color)",
              fontSize: '11px'
            }}
          >
            {connector.category}
          </span>
          <span 
            className="badge" 
            style={{ 
              backgroundColor: "rgba(255,255,255,0.04)",
              color: ORANGE_COLORS.primary,
              border: "1px solid var(--vz-border-color)",
              fontSize: '11px'
            }}
          >
            {connector.connectionType}
          </span>
        </div>
        
        <div className="d-flex justify-content-between align-items-center py-2 mb-3 border-top border-bottom" style={{ borderColor: 'var(--vz-border-color) !important' }}>
          <div>
            <p className="text-muted small mb-0">Last Sync</p>
            <p className="mb-0 fw-semibold small">{formatLastSync(connector.lastSync)}</p>
          </div>
          <div className="text-end">
            <p className="text-muted small mb-0">Events (24h)</p>
            <p className="mb-0 fw-semibold small">
              {connector.dataCollected > 0 ? connector.dataCollected.toLocaleString() : '—'}
            </p>
          </div>
        </div>
        
        <div className="d-flex gap-2">
          {connector.status === "Active" ? (
            <>
              <Button
                size="sm"
                className="flex-grow-1"
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${ORANGE_COLORS.primary}`,
                  color: ORANGE_COLORS.primary,
                  fontSize: '12px'
                }}
                onClick={() => onViewDetails(connector)}
              >
                <FeatherIcon icon="eye" size={14} className="me-1" />
                View Details
              </Button>
              <Button
                size="sm"
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid rgba(255, 107, 53, 0.2)`,
                  color: ORANGE_COLORS.primary,
                  fontSize: '12px'
                }}
                onClick={() => onTest(connector)}
              >
                <FeatherIcon icon="activity" size={14} />
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              className="w-100"
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${ORANGE_COLORS.primary}`,
                color: ORANGE_COLORS.primary,
                fontSize: '12px'
              }}
              onClick={() => onConfigure(connector)}
            >
              <FeatherIcon icon="settings" size={14} className="me-1" />
              Configure
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const Connectors = () => {
  document.title = "Data Connectors | Security Dashboard";
  
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  
  const fetchConnectors = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setConnectors(MOCK_CONNECTORS);
    } catch (error) {
      console.error("Error fetching connectors:", error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchConnectors();
  }, []);
  
  const stats = {
    total: connectors.length,
    active: connectors.filter(c => c.status === "Active").length,
    inactive: connectors.filter(c => c.status === "Inactive").length,
    notConfigured: connectors.filter(c => c.status === "Not Configured").length,
    totalDataCollected: connectors.reduce((sum, c) => sum + c.dataCollected, 0)
  };

  const statsConfig = [
    {
      label: "Total Connectors",
      value: stats.total,
      icon: "link",
      color: ORANGE_COLORS.primary,
      borderColor: 'rgba(255, 107, 53, 0.2)'
    },
    {
      label: "Active",
      value: stats.active,
      icon: "check-circle",
      color: '#198754',
      borderColor: 'rgba(25, 135, 84, 0.2)'
    },
    {
      label: "Inactive",
      value: stats.inactive,
      icon: "alert-circle",
      color: '#ffc107',
      borderColor: 'rgba(255, 193, 7, 0.2)'
    },
    {
      label: "Events (24h)",
      value: stats.totalDataCollected,
      icon: "activity",
      color: ORANGE_COLORS.dark,
      borderColor: 'rgba(232, 90, 40, 0.2)'
    }
  ];
  
  const categories = ["All", ...new Set(connectors.map(c => c.category))];
  
  const filteredConnectors = connectors.filter(connector => {
    const matchesSearch = connector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         connector.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "All" || connector.status === filterStatus;
    const matchesCategory = filterCategory === "All" || connector.category === filterCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  
  const handleAddConnector = () => setShowAddModal(true);
  const handleConfigure = (connector) => {
    setSelectedConnector(connector);
    setShowConfigureModal(true);
  };
  const handleTest = (connector) => {
    setSelectedConnector(connector);
    setShowTestModal(true);
  };
  const handleViewDetails = (connector) => {
    setSelectedConnector(connector);
    setShowDetailsModal(true);
  };
  
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner style={{ color: ORANGE_COLORS.primary, width: '3rem', height: '3rem' }} />
            <p className="text-muted mt-3">Loading connectors...</p>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Page Title */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div style={darkIconChip}>
                    <i className="ri-share-line" style={darkIconGlyph}></i>
                  </div>
                  <div>
                    <h4 className="mb-0" style={pageHeadingStyle}>
                      Data Source Connectors
                    </h4>
                  </div>
                </div>
                <Button
                  style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${ORANGE_COLORS.primary}`,
                    color: ORANGE_COLORS.primary
                  }}
                  onClick={handleAddConnector}
                >
                  <FeatherIcon icon="plus-circle" size={16} className="me-2" />
                  Add Connector
                </Button>
              </div>
            </Col>
          </Row>

          {/* Stats Tiles */}
          <Row className="mb-4">
            {statsConfig.map((stat, idx) => (
              <Col xl={3} md={6} sm={6} xs={12} key={idx}>
                <StatTile {...stat} />
              </Col>
            ))}
          </Row>
          
          {/* Filters Card */}
          <Card 
            className="mb-3"
            style={{ 
              ...cardShell
            }}
          >
            <CardBody>
              <Row className="g-3">
                <Col md={5}>
                  <InputGroup>
                    <span className="input-group-text bg-transparent" style={{ borderColor: 'rgba(255, 107, 53, 0.2)' }}>
                      <FeatherIcon icon="search" size={16} className="text-muted" />
                    </span>
                    <Input
                      type="text"
                      placeholder="Search connectors..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ 
                        borderColor: 'rgba(255, 107, 53, 0.2)',
                        backgroundColor: 'transparent'
                      }}
                    />
                  </InputGroup>
                </Col>
                <Col md={7} className="d-flex gap-2 justify-content-end">
                  <Input
                    type="select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{ 
                      width: "auto",
                      borderColor: 'rgba(255, 107, 53, 0.2)',
                      backgroundColor: '#0b1220'
                    }}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Not Configured">Not Configured</option>
                  </Input>
                  <Input
                    type="select"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    style={{ 
                      width: "auto",
                      borderColor: 'rgba(255, 107, 53, 0.2)',
                      backgroundColor: '#0b1220'
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat === "All" ? "All Categories" : cat}
                      </option>
                    ))}
                  </Input>
                  <Button 
                    style={{
                      backgroundColor: 'transparent',
                      border: `1px solid rgba(255, 107, 53, 0.2)`,
                      color: ORANGE_COLORS.primary
                    }}
                    onClick={fetchConnectors}
                  >
                    <FeatherIcon icon="refresh-cw" size={16} />
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
          
          {/* Connectors Grid */}
          {filteredConnectors.length === 0 ? (
            <Card 
              style={{ 
                ...cardShell
              }}
            >
              <CardBody className="text-center py-5">
                <FeatherIcon icon="search" size={48} className="text-muted mb-3" />
                <h5 className="text-muted">No connectors found</h5>
                <p className="text-muted small">
                  {searchTerm
                    ? `No results matching "${searchTerm}". Try adjusting your filters.`
                    : "Try adjusting your filters to see more connectors."}
                </p>
              </CardBody>
            </Card>
          ) : (
            <Row>
              {filteredConnectors.map((connector) => (
                <Col xl={3} lg={4} md={6} key={connector.id}>
                  <ConnectorCard
                    connector={connector}
                    onConfigure={handleConfigure}
                    onTest={handleTest}
                    onViewDetails={handleViewDetails}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </div>
      
      {/* Add Connector Modal */}
      <Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShowAddModal(false)}>
          <div className="d-flex align-items-center">
            <div 
              className="d-flex align-items-center justify-content-center me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                backgroundColor: `${ORANGE_COLORS.primary}15`
              }}
            >
              <span style={darkIconChip}>
                <FeatherIcon icon="plus-circle" size={15} style={darkIconGlyph} />
              </span>
            </div>
            <span>Add New Connector</span>
          </div>
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-4">Select a data source to connect to your security platform</p>
          
          <Row className="g-3 ">
            {MOCK_CONNECTORS.slice(0, 6).map((connector) => (
              <Col md={6} key={connector.id}>
                <div 
                  className="p-3 border rounded d-flex align-items-center gap-3"
                  style={{ 
                    cursor: "pointer", 
                    transition: "all 0.2s",
                    borderColor: 'rgba(255, 107, 53, 0.2)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = ORANGE_COLORS.subtle}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                <div 
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 91, 0, 0.35)",
                    backgroundColor: "rgba(255, 91, 0, 0.12)",
                    flexShrink: 0
                  }}
                >
                    <FeatherIcon icon={connector.icon} size={15} style={{ color: "#FF5B00" }} />
                </div>
                  <div>
                    <h6 className="mb-0" style={{ fontSize: "14px" }}>{connector.name}</h6>
                    <small className="text-muted">{connector.type}</small>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          
          <div 
            className="mt-4 p-3 rounded" 
            style={{ 
              backgroundColor: 'rgba(255, 107, 53, 0.1)',
              border: `1px solid rgba(255, 107, 53, 0.2)`
            }}
          >
            <small style={{ color: ORANGE_COLORS.primary }}>
              <FeatherIcon icon="info" size={14} className="me-2" />
              Don't see your connector? Contact support to request a custom integration.
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button 
            style={{
              backgroundColor: 'transparent',
              border: `1px solid rgba(255, 107, 53, 0.2)`,
              color: ORANGE_COLORS.primary
            }}
            onClick={() => setShowAddModal(false)}
          >
            Cancel
          </Button>
          <Button 
            style={{
              backgroundColor: ORANGE_COLORS.primary,
              border: 'none',
              color: 'white'
            }}
          >
            <FeatherIcon icon="arrow-right" size={16} className="me-2" />
            Continue
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Configure Modal */}
      <Modal isOpen={showConfigureModal} toggle={() => setShowConfigureModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShowConfigureModal(false)}>
          <div className="d-flex align-items-center">
            {selectedConnector && (
              <>
                <div 
                  className="d-flex align-items-center justify-content-center me-3"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 91, 0, 0.35)",
                    backgroundColor: "rgba(255, 91, 0, 0.12)"
                  }}
                >
                  <FeatherIcon icon={selectedConnector.icon} size={15} style={{ color: "#FF5B00" }} />
                </div>
                <span>Configure {selectedConnector.name}</span>
              </>
            )}
          </div>
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Connection Name</Label>
              <Input 
                type="text" 
                placeholder="Enter a friendly name" 
                defaultValue={selectedConnector?.name}
                style={{ 
                  borderColor: 'rgba(255, 107, 53, 0.2)',
                  backgroundColor: 'transparent'
                }}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>API Endpoint</Label>
              <Input 
                type="text" 
                placeholder="https://api.example.com"
                style={{ 
                  borderColor: 'rgba(255, 107, 53, 0.2)',
                  backgroundColor: 'transparent'
                }}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>API Key / Token</Label>
              <Input 
                type="password" 
                placeholder="Enter your API key"
                style={{ 
                  borderColor: 'rgba(255, 107, 53, 0.2)',
                  backgroundColor: 'transparent'
                }}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Sync Interval</Label>
              <Input 
                type="select"
                style={{ 
                  borderColor: 'rgba(255, 107, 53, 0.2)',
                  backgroundColor: 'transparent'
                }}
              >
                <option>Every 5 minutes</option>
                <option>Every 15 minutes</option>
                <option>Every 30 minutes</option>
                <option>Every hour</option>
              </Input>
            </FormGroup>
            
            <div 
              className="mt-3 p-3 rounded" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)',
                border: `1px solid rgba(255, 107, 53, 0.2)`
              }}
            >
              <small style={{ color: ORANGE_COLORS.primary }}>
                <FeatherIcon icon="info" size={14} className="me-2" />
                Configuration settings will be saved securely. You can test the connection before saving.
              </small>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button 
            style={{
              backgroundColor: 'transparent',
              border: `1px solid rgba(255, 107, 53, 0.2)`,
              color: ORANGE_COLORS.primary
            }}
            onClick={() => setShowConfigureModal(false)}
          >
            Cancel
          </Button>
          <Button 
            style={{
              backgroundColor: ORANGE_COLORS.primary,
              border: 'none',
              color: 'white'
            }}
            onClick={() => {
              setShowConfigureModal(false);
            }}
          >
            <FeatherIcon icon="save" size={16} className="me-2" />
            Save Configuration
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Test Connection Modal */}
      <Modal isOpen={showTestModal} toggle={() => setShowTestModal(false)} centered>
        <ModalHeader toggle={() => setShowTestModal(false)}>
          Test Connection
        </ModalHeader>
        <ModalBody>
          {selectedConnector && (
            <div className="text-center py-4">
              <div 
                className="d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 91, 0, 0.35)",
                  backgroundColor: "rgba(255, 91, 0, 0.12)"
                }}
              >
                <FeatherIcon icon={selectedConnector.icon} size={15} style={{ color: "#FF5B00" }} />
              </div>
              <h5 className="mb-2">{selectedConnector.name}</h5>
              
              <div className="my-4">
                <Spinner style={{ color: ORANGE_COLORS.primary, width: "2rem", height: "2rem" }} />
                <p className="mt-3 text-muted">Testing connection...</p>
              </div>
              
              <div className="p-3 rounded" style={{ backgroundColor: `${ORANGE_COLORS.primary}15` }}>
                <FeatherIcon icon="check-circle" size={20} className="me-2" style={{ color: ORANGE_COLORS.primary }} />
                <span className="fw-semibold" style={{ color: ORANGE_COLORS.primary }}>Connection Successful!</span>
                <p className="mb-0 mt-2 small text-muted">
                  Successfully connected to {selectedConnector.name}. Data collection will begin shortly.
                </p>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            style={{
              backgroundColor: ORANGE_COLORS.primary,
              border: 'none',
              color: 'white'
            }}
            onClick={() => setShowTestModal(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* View Details Modal */}
      <Modal isOpen={showDetailsModal} toggle={() => setShowDetailsModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShowDetailsModal(false)}>
          <div className="d-flex align-items-center">
            {selectedConnector && (
              <>
                <div 
                  className="d-flex align-items-center justify-content-center me-3"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 91, 0, 0.35)",
                    backgroundColor: "rgba(255, 91, 0, 0.12)"
                  }}
                >
                  <FeatherIcon icon={selectedConnector.icon} size={15} style={{ color: "#FF5B00" }} />
                </div>
                <span>{selectedConnector.name} Details</span>
              </>
            )}
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedConnector && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Status</small>
                    <span 
                      className="badge" 
                      style={{ 
                        backgroundColor: `${ORANGE_COLORS.primary}15`,
                        color: ORANGE_COLORS.primary
                      }}
                    >
                      {selectedConnector.status}
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Connection Type</small>
                    <span className="fw-semibold">{selectedConnector.connectionType}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Category</small>
                    <span className="fw-semibold">{selectedConnector.category}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">Events Collected (24h)</small>
                    <span className="fw-semibold">{selectedConnector.dataCollected.toLocaleString()}</span>
                  </div>
                </Col>
              </Row>
              
              <div className="mb-3">
                <h6 className="fw-semibold mb-2">Description</h6>
                <p className="text-muted">{selectedConnector.description}</p>
              </div>
              
              <div className="mb-3">
                <h6 className="fw-semibold mb-2">Recent Activity</h6>
                <div 
                  className="border rounded p-3"
                  style={{ borderColor: 'rgba(255, 107, 53, 0.2)' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small>Last Sync</small>
                    <small className="fw-semibold">{formatLastSync(selectedConnector.lastSync)}</small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small>Sync Status</small>
                    <small className="fw-semibold" style={{ color: ORANGE_COLORS.primary }}>
                      <FeatherIcon icon="check-circle" size={14} className="me-1" />
                      Successful
                    </small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small>Next Sync</small>
                    <small className="fw-semibold">In 15 minutes</small>
                  </div>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            style={{
              backgroundColor: 'transparent',
              border: `1px solid rgba(255, 107, 53, 0.2)`,
              color: ORANGE_COLORS.primary
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
          <Button 
            style={{
              backgroundColor: ORANGE_COLORS.primary,
              border: 'none',
              color: 'white'
            }}
            onClick={() => {
              setShowDetailsModal(false);
              handleConfigure(selectedConnector);
            }}
          >
            <FeatherIcon icon="settings" size={16} className="me-2" />
            Configure
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Connectors;
